import { CaseCheckerPendingCasesResponse } from 'utils/types';
import { AxiosResponse } from 'axios';
import { axiosInstance } from 'hooks/auth/AxiosInstance';

import { UseQueryResult, useQuery } from '@tanstack/react-query';

type PageType = {
  pageSize: string;
  page: string;
  filters?: Record<string, string>;
  enabled?: boolean;
  refetchInterval?: number;
};

type Options = {
  options: PageType;
};

const fetchData = async ({
  options: { page, pageSize, filters = {} },
}: Options): Promise<CaseCheckerPendingCasesResponse> => {
  try {
    const queryParams = new URLSearchParams({
      ...filters,
      page,
      size: pageSize,
    }).toString();

    const response: AxiosResponse<CaseCheckerPendingCasesResponse> =
      await axiosInstance.get(
        `fine/case-checker/pending${queryParams ? `?${queryParams}` : ''}`,
      );
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error('An unknown error occurred');
    }
  }
};

const useGetCaseCheckerPendingCases = ({
  options,
}: Options): UseQueryResult<CaseCheckerPendingCasesResponse, Error> => {
  const resolvedOptions: PageType = {
    page: options.page ?? '0',
    pageSize: options.pageSize ?? '20',
    filters: options.filters ?? {},
    enabled: options.enabled,
    refetchInterval: options.refetchInterval ?? 10000,
  };

  return useQuery<CaseCheckerPendingCasesResponse, Error>({
    queryKey: [
      'case-checker-pending-cases',
      resolvedOptions.page,
      resolvedOptions.pageSize,
      resolvedOptions.filters,
    ],
    queryFn: () => fetchData({ options: resolvedOptions }),
    refetchOnWindowFocus: false,
    staleTime: 0,
    enabled: resolvedOptions.enabled,
    refetchInterval: resolvedOptions.refetchInterval,
  });
};

export default useGetCaseCheckerPendingCases;
